import Component from "@base/Component.js";
import api from "@api/api.js";
import config from "@/config.js";
import { lockScroll, unlockScroll } from "@/services/scrollLock.js";
import RichTextEditor from "@/components/RichTextEditor.js";
import t from "@/services/translationService.js";

// Batch caps for immediate upload, mirroring GalleryEditor: the backend
// converts each image synchronously, so one request holding a whole folder can
// outlast the reverse proxy's read timeout. Capped by bytes as well as count
// because a handful of 60MB originals adds up as fast as many small ones.
const UPLOAD_BATCH_MAX_FILES = 40;
const UPLOAD_BATCH_MAX_BYTES = 400 * 1024 * 1024;

/**
 * Admin create/edit overlay for a gear item. Mount to document.body.
 * props: { item: ShowcaseObject|null, onDone() }
 *
 * Produces the FormData the backend expects (POST /api/showcase/:id):
 * id, accessType, en_/de_ title/text, a {language}_detail per config.languages,
 * image (the chosen title image),
 * imagesToKeep (JSON). Images themselves are uploaded as they are picked, via
 * POST /api/showcase/:id/images — same two-step flow as the gallery editor, so
 * adding photos never has to fit inside the save request.
 */
export default class ItemEditor extends Component {
  constructor(props) {
    super(props);
    const it = props.item;
    this.isNew = !it;
    this.gearItemId = it?.gearItemId || crypto.randomUUID();
    this.existingImages = it?.imageNames?.() || [];
    this.removed = new Set();
    this.addedThisSession = new Set(); // uploaded during this edit, for the "new" badge
    this.data = {
      accessType: it?.accessType ?? 2,
      image: it?.image || null,
      en_title: it?.en_title || "",
      en_text: it?.en_content || "",
      de_title: it?.de_title || "",
      de_text: it?.de_content || "",
    };
    // The detail bodies live in one map rather than as en_/de_ fields, so the
    // single tabbed editor below and the save payload both come from
    // config.languages — adding a language touches neither.
    this.detailByLanguage = Object.fromEntries(
      config.languages.map((language) => [language, it?.[`${language}_detail`] || ""])
    );
    this.detailLanguage = config.languages[0];
  }

  render() {
    const d = this.data;
    const val = (v) => Component.escape(v);

    const imgs = this.existingImages
      .map((name) => {
        const isTitle = d.image === name;
        const cls = [
          "editor-thumb",
          this.removed.has(name) && "editor-thumb--removed",
          this.addedThisSession.has(name) && "editor-thumb--new",
        ]
          .filter(Boolean)
          .join(" ");
        return `
        <div class="${cls}" data-name="${val(name)}">
          <img src="${config.imageBase}/showcase/${encodeURIComponent(this.gearItemId)}/${encodeURIComponent(name)}?size=thumb" alt="" loading="lazy" />
          <button type="button" class="editor-thumb__toggle" data-name="${val(name)}">${this.removed.has(name) ? "↺" : "×"}</button>
          <button type="button" class="editor-thumb__star${isTitle ? " editor-thumb__star--active" : ""}" data-name="${val(name)}" title="${val(isTitle ? t.t("cockpit.gear.editor.titleImage") : t.t("cockpit.gear.editor.setTitleImage"))}">★</button>
        </div>`;
      })
      .join("");

    return `
      <div class="modal-backdrop">
        <div class="modal-card modal-card--wide">
          <div class="modal-header">
            <h2>${val(this.isNew ? t.t("cockpit.gear.add") : t.t("cockpit.gear.editor.edit"))}</h2>
            <button type="button" class="modal-close" aria-label="${val(t.t("common.close"))}">&times;</button>
          </div>
          <form class="editor-form">
            <div class="editor-cols">
              <fieldset>
                <legend>${val(t.t("common.languageEn"))}</legend>
                <label>${val(t.t("cockpit.gear.editor.title"))}<input name="en_title" value="${val(d.en_title)}" required /></label>
                <label>${val(t.t("cockpit.gear.editor.shortText"))}<textarea name="en_text" rows="4">${val(d.en_text)}</textarea></label>
              </fieldset>
              <fieldset>
                <legend>${val(t.t("common.languageDe"))}</legend>
                <label>${val(t.t("cockpit.gear.editor.title"))}<input name="de_title" value="${val(d.de_title)}" required /></label>
                <label>${val(t.t("cockpit.gear.editor.shortText"))}<textarea name="de_text" rows="4">${val(d.de_text)}</textarea></label>
              </fieldset>
            </div>
            <p class="editor-hint">${val(t.t("cockpit.gear.editor.textHint"))}</p>

            <div class="editor-detail-slot"></div>

            <div class="editor-meta">
              <label>${val(t.t("cockpit.gear.editor.visibility"))}
                <select name="accessType">
                  <option value="2" ${d.accessType === 2 ? "selected" : ""}>${val(t.t("cockpit.visibility.draft"))}</option>
                  <option value="1" ${d.accessType === 1 ? "selected" : ""}>${val(t.t("cockpit.visibility.public"))}</option>
                </select>
              </label>
            </div>

            <div class="editor-images">
              <div class="editor-thumbs">${imgs}</div>
              <label class="editor-file">${val(t.t("cockpit.gear.editor.addImages"))}<input type="file" name="images" accept=".jpg,.jpeg,.png" multiple /></label>
              <div class="editor-upload-progress" hidden>
                <div class="editor-upload-progress__bar"></div>
              </div>
              <p class="editor-hint">${val(t.t("cockpit.gear.editor.uploadHint"))}</p>
            </div>

            <div class="form-error" role="alert"></div>
            <div class="editor-actions">
              <button type="submit" class="btn-primary">${val(t.t("common.save"))}</button>
              ${this.isNew ? "" : `<button type="button" class="btn-danger" data-action="delete">${val(t.t("common.delete"))}</button>`}
              <button type="button" class="btn-secondary" data-action="cancel">${val(t.t("common.cancel"))}</button>
            </div>
          </form>
        </div>
      </div>`;
  }

  async onMounted() {
    lockScroll();
    const backdrop = this.el;
    const errorEl = this.$(".form-error");
    const close = () => this.unmount();

    this.listen(this.$(".modal-close"), "click", close);
    this.listen(this.$('[data-action="cancel"]'), "click", close);
    this.listen(backdrop, "click", (e) => e.target === backdrop && close());
    // No Escape-to-close here (the gallery editor has none either): this form
    // holds a long-form write-up per language, and a stray Escape mid-sentence
    // throwing them away is a worse failure than one extra click to cancel.

    // Enter inside a plain input would otherwise submit and close the form.
    // Textareas and the rich-text bodies keep their newline behaviour, and
    // buttons keep working from the keyboard.
    this.listen(this.$(".editor-form"), "keydown", (e) => {
      if (e.key !== "Enter") return;
      if (e.target.tagName === "INPUT" && e.target.type !== "file") e.preventDefault();
    });

    // One editor for every language: its tabs pick which translation the
    // toolbar and text area are editing.
    this.detailEditor = await this.mountChild(
      RichTextEditor,
      {
        htmlByLanguage: this.detailByLanguage,
        languages: config.languages,
        activeLanguage: this.detailLanguage,
        label: t.t("cockpit.gear.editor.detailText"),
      },
      this.$(".editor-detail-slot")
    );

    this.listen(this.$('input[type="file"]'), "change", (e) => this.#upload(e, errorEl));

    // Toggle removal of an image; the file itself goes on save, via imagesToKeep.
    this.$$(".editor-thumb__toggle").forEach((btn) => {
      this.listen(btn, "click", () => {
        const name = btn.getAttribute("data-name");
        if (this.removed.has(name)) this.removed.delete(name);
        else this.removed.add(name);
        // Removing the title image clears the pick rather than leaving a
        // reference to a file that won't exist after save.
        if (this.removed.has(name) && this.data.image === name) this.data.image = null;
        this.#reflectImageState();
      });
    });

    this.$$(".editor-thumb__star").forEach((btn) => {
      this.listen(btn, "click", () => {
        const name = btn.getAttribute("data-name");
        this.data.image = this.data.image === name ? null : name;
        this.#reflectImageState();
      });
    });

    const delBtn = this.$('[data-action="delete"]');
    if (delBtn) this.listen(delBtn, "click", () => this.#delete(errorEl));

    this.listen(this.$(".editor-form"), "submit", (e) => {
      e.preventDefault();
      this.#save(errorEl);
    });
  }

  /**
   * Repaint the thumbnails from `removed` / `data.image`.
   *
   * A targeted DOM patch rather than a full setState() re-render — the same
   * reasoning as GalleryEditor's place list. Re-rendering replaces the whole
   * modal, which resets the card's scroll position and drops the admin back at
   * the top of a long form every time they cross out one photo.
   */
  #reflectImageState() {
    for (const thumb of this.$$(".editor-thumb")) {
      const name = thumb.getAttribute("data-name");
      const isRemoved = this.removed.has(name);
      const isTitle = this.data.image === name;
      thumb.classList.toggle("editor-thumb--removed", isRemoved);
      const toggle = thumb.querySelector(".editor-thumb__toggle");
      if (toggle) toggle.textContent = isRemoved ? "↺" : "×";
      const star = thumb.querySelector(".editor-thumb__star");
      if (star) {
        star.classList.toggle("editor-thumb__star--active", isTitle);
        star.title = isTitle ? t.t("cockpit.gear.editor.titleImage") : t.t("cockpit.gear.editor.setTitleImage");
      }
    }
  }

  /** Split a picked selection into batches small enough for one request each. */
  #buildBatches(files) {
    const batches = [];
    let current = [];
    let currentBytes = 0;
    for (const f of files) {
      if (current.length && (current.length >= UPLOAD_BATCH_MAX_FILES || currentBytes + f.size > UPLOAD_BATCH_MAX_BYTES)) {
        batches.push(current);
        current = [];
        currentBytes = 0;
      }
      current.push(f);
      currentBytes += f.size;
    }
    if (current.length) batches.push(current);
    return batches;
  }

  async #upload(e, errorEl) {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const input = e.target;
    const labelText = this.$(".editor-file").firstChild;
    const originalLabel = labelText.textContent;
    const progressEl = this.$(".editor-upload-progress");
    const progressBar = this.$(".editor-upload-progress__bar");
    input.disabled = true;
    errorEl.textContent = "";
    progressEl.hidden = false;
    progressBar.style.width = "0%";

    const batches = this.#buildBatches(picked);
    const totalBytes = picked.reduce((sum, f) => sum + f.size, 0);
    let doneFiles = 0;
    let doneBytes = 0;

    try {
      for (const batch of batches) {
        const batchBytes = batch.reduce((sum, f) => sum + f.size, 0);
        const fd = new FormData();
        batch.forEach((f) => fd.append("images", f));
        const res = await api.postFormWithProgress(
          `/api/showcase/${encodeURIComponent(this.gearItemId)}/images`,
          fd,
          (loaded) => {
            const pct = totalBytes ? Math.min(100, Math.round(((doneBytes + loaded) / totalBytes) * 100)) : 0;
            progressBar.style.width = `${pct}%`;
            labelText.textContent =
              batches.length > 1
                ? t
                    .t("common.uploadingBatch")
                    .replace("{pct}", pct)
                    .replace("{done}", doneFiles)
                    .replace("{total}", picked.length)
                : t.t("common.uploading").replace("{pct}", pct);
          }
        );
        doneBytes += batchBytes;
        doneFiles += batch.length;
        for (const name of res?.uploadedImages || []) {
          if (!this.existingImages.includes(name)) this.existingImages.push(name);
          this.addedThisSession.add(name);
        }
      }
      this.#syncDataFromForm();
      this.setStateKeepingScroll({}, ".modal-card"); // rebuilds the form fresh, so no manual reset on success
    } catch (err) {
      errorEl.textContent = err.message || t.t("cockpit.gear.editor.errUpload");
      input.disabled = false;
      labelText.textContent = originalLabel;
      progressEl.hidden = true;
    }
  }

  // setState() rebuilds the form from this.data, so anything typed since mount
  // must be captured first — including every language's detail body, or adding
  // an image mid-edit would silently discard the write-up.
  #syncDataFromForm() {
    const form = this.$(".editor-form");
    if (!form) return;
    Object.assign(this.data, {
      accessType: Number(form.accessType.value),
      en_title: form.en_title.value,
      en_text: form.en_text.value,
      de_title: form.de_title.value,
      de_text: form.de_text.value,
    });
    if (this.detailEditor) {
      this.detailByLanguage = this.detailEditor.getHtmlByLanguage();
      this.detailLanguage = this.detailEditor.activeLanguage;
    }
  }

  #collect() {
    this.#syncDataFromForm();
    const d = this.data;
    const fd = new FormData();
    fd.append("id", this.gearItemId);
    fd.append("accessType", String(d.accessType));
    fd.append("en_title", d.en_title);
    fd.append("en_text", d.en_text);
    fd.append("de_title", d.de_title);
    fd.append("de_text", d.de_text);
    for (const language of config.languages) fd.append(`${language}_detail`, this.detailByLanguage[language] ?? "");
    // Images are already on disk — save only says which names survive.
    const keep = this.existingImages.filter((n) => !this.removed.has(n));
    fd.append("imagesToKeep", JSON.stringify(keep));
    if (d.image && keep.includes(d.image)) fd.append("image", d.image);
    return fd;
  }

  async #save(errorEl) {
    const submit = this.$('button[type="submit"]');
    errorEl.textContent = "";
    submit.disabled = true;
    submit.textContent = t.t("cockpit.gear.editor.saving");
    try {
      await api.post(`/api/showcase/${encodeURIComponent(this.gearItemId)}`, this.#collect(), { isForm: true });
      this.finalized = true;
      this.props.onDone?.();
      this.unmount();
    } catch (err) {
      errorEl.textContent = err.message || t.t("cockpit.gear.editor.errSave");
      submit.disabled = false;
      submit.textContent = t.t("common.save");
    }
  }

  async #delete(errorEl) {
    if (!window.confirm(t.t("cockpit.gear.editor.confirmDelete"))) return;
    try {
      await api.del(`/api/showcase/${encodeURIComponent(this.gearItemId)}`);
      this.finalized = true;
      this.props.onDone?.();
      this.unmount();
    } catch (err) {
      errorEl.textContent = err.message || t.t("cockpit.gear.editor.errDelete");
    }
  }

  onBeforeUnmount() {
    unlockScroll();
    // Images upload immediately, so a brand-new item closed without ever being
    // saved would leave an orphaned images/showcase/<id>/ folder with no DB row.
    // Existing items are never touched here — their images are legitimately live,
    // and the endpoint refuses them anyway.
    if (this.isNew && !this.finalized && this.existingImages.length) {
      api.del(`/api/showcase/${encodeURIComponent(this.gearItemId)}/images`).catch(() => {});
    }
  }
}
